import type { ApiResponse, PageMeta } from '@salon/shared';

const BASE = `${import.meta.env.VITE_API_URL ?? ''}/api`;

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }

  /** Field errors from a Zod `flatten()` payload, if any. */
  get fieldErrors(): Record<string, string[]> {
    const d = this.details as { fieldErrors?: Record<string, string[]> } | undefined;
    return d?.fieldErrors ?? {};
  }
}

export interface Paged<T> {
  items: T[];
  meta: PageMeta;
}

let refreshing: Promise<boolean> | null = null;
let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(fn: () => void) {
  onSessionExpired = fn;
}

/** Single-flight refresh so parallel 401s trigger only one rotation. */
function refreshSession(): Promise<boolean> {
  refreshing ??= fetch(`${BASE}/auth/refresh`, { method: 'POST', credentials: 'include' })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      setTimeout(() => (refreshing = null), 0);
    });
  return refreshing;
}

type Query = Record<string, string | number | boolean | undefined | null | string[]>;

function buildUrl(path: string, query?: Query) {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === '') continue;
    url.searchParams.set(k, Array.isArray(v) ? v.join(',') : String(v));
  }
  return url.toString();
}

async function request<T>(
  method: string,
  path: string,
  opts: { body?: unknown; query?: Query; retry?: boolean } = {},
): Promise<{ data: T; meta: ApiResponse<T>['meta'] }> {
  const isForm = opts.body instanceof FormData;
  const res = await fetch(buildUrl(path, opts.query), {
    method,
    credentials: 'include',
    headers: opts.body && !isForm ? { 'Content-Type': 'application/json' } : undefined,
    body: opts.body === undefined ? undefined : isForm ? (opts.body as FormData) : JSON.stringify(opts.body),
  });

  if (res.status === 401 && opts.retry !== false && !path.startsWith('/auth/')) {
    if (await refreshSession()) return request<T>(method, path, { ...opts, retry: false });
    onSessionExpired?.();
  }

  const json = (await res.json().catch(() => null)) as ApiResponse<T> | null;
  if (!res.ok || !json?.success) {
    throw new ApiError(
      res.status,
      json?.error?.code ?? 'NETWORK',
      json?.error?.message ?? (res.status === 0 ? 'Network error' : res.statusText || 'Request failed'),
      json?.error?.details,
    );
  }
  return { data: json.data as T, meta: json.meta };
}

export const api = {
  get: async <T>(path: string, query?: Query) => (await request<T>('GET', path, { query })).data,
  page: async <T>(path: string, query?: Query): Promise<Paged<T>> => {
    const r = await request<T[]>('GET', path, { query });
    return { items: r.data, meta: r.meta as PageMeta };
  },
  post: async <T>(path: string, body?: unknown) => (await request<T>('POST', path, { body: body ?? {} })).data,
  put: async <T>(path: string, body?: unknown) => (await request<T>('PUT', path, { body })).data,
  patch: async <T>(path: string, body?: unknown) => (await request<T>('PATCH', path, { body })).data,
  del: async <T>(path: string) => (await request<T>('DELETE', path)).data,
  upload: async <T>(path: string, file: File, fields: Record<string, string> = {}) => {
    const form = new FormData();
    form.append('file', file);
    Object.entries(fields).forEach(([k, v]) => form.append(k, v));
    return (await request<T>('POST', path, { body: form })).data;
  },
  /** Authenticated file download (e.g. CSV export). */
  download: async (path: string, filename: string) => {
    let res = await fetch(buildUrl(path), { credentials: 'include' });
    if (res.status === 401 && (await refreshSession())) res = await fetch(buildUrl(path), { credentials: 'include' });
    if (!res.ok) throw new ApiError(res.status, 'DOWNLOAD_FAILED', 'Download failed');
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  },
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const fields = Object.entries(err.fieldErrors);
    if (fields.length) return `${err.message}: ${fields.map(([k, v]) => `${k} ${v[0]}`).join('; ')}`;
    return err.message;
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}
